import { Component, OnInit } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormBuilder, FormControl, ReactiveFormsModule, Validators } from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatDatepickerModule } from "@angular/material/datepicker";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { MatNativeDateModule } from "@angular/material/core";
import { MatSelectModule } from "@angular/material/select";
import { Router, RouterModule } from "@angular/router";
import { forkJoin, of } from "rxjs";
import { catchError, finalize, map, startWith, switchMap } from "rxjs/operators";
import { NgxMatSelectSearchModule } from "ngx-mat-select-search";
import { Empleado } from "src/app/shared/model/empleado/empleado.model";
import { HorarioLaboral } from "src/app/shared/model/tarea/horario-laboral.model";
import { EmpleadoHorario } from "src/app/shared/model/tarea/empleado-horario.model";
import { JornadaLaboral } from "src/app/shared/model/tarea/jornada-laboral.model";
import { SweetAlertComponent } from "src/app/shared/SweetMesssage";
import { EmpleadoService } from "src/app/empleado/empleado.service";
import { HorarioLaboralService } from "../horario-laboral.service";

interface EmpleadoHorarioResumen {
  empleado: Empleado;
  asignacion: EmpleadoHorario;
}

@Component({
  selector: "app-asignacion-horario",
  standalone: true,
  templateUrl: "./asignacion-horario.component.html",
  styleUrls: ["./asignacion-horario.component.css"],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterModule,
    MatButtonModule,
    MatDatepickerModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatNativeDateModule,
    MatSelectModule,
    NgxMatSelectSearchModule,
  ],
})
export class AsignacionHorarioComponent implements OnInit {
  form;

  readonly empleadoFilterCtrl = new FormControl("");

  empleados: Empleado[] = [];
  empleadosFiltrados: Empleado[] = [];
  horarios: HorarioLaboral[] = [];
  asignacionesEmpleado: EmpleadoHorario[] = [];
  empleadosDelHorario: EmpleadoHorarioResumen[] = [];
  jornadaActual: JornadaLaboral | null = null;
  horarioSeleccionado: HorarioLaboral | null = null;

  loading = false;
  saving = false;
  loadingHorarioDetalle = false;
  loadingEmpleadosHorario = false;

  private readonly asignacionesCache = new Map<number, EmpleadoHorario[]>();

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private horarioService: HorarioLaboralService,
    private empleadoService: EmpleadoService,
    private sweetAlert: SweetAlertComponent
  ) {
    this.form = this.fb.group({
      idEmpleado: [null as number | null, Validators.required],
      idHorarioLaboral: [null as number | null, Validators.required],
      fechaInicioVigencia: [new Date(), Validators.required],
    });
  }

  ngOnInit(): void {
    this.cargarCatalogos();

    this.empleadoFilterCtrl.valueChanges
      .pipe(startWith(""))
      .subscribe((value) => this.filtrarEmpleados(value ?? ""));

    this.form.get("idEmpleado")?.valueChanges.subscribe((idEmpleado) => {
      if (idEmpleado) {
        this.consultarAsignaciones(idEmpleado);
        this.consultarJornada(idEmpleado);
      } else {
        this.asignacionesEmpleado = [];
        this.jornadaActual = null;
      }
    });

    this.form.get("idHorarioLaboral")?.valueChanges.subscribe((idHorarioLaboral) => {
      this.cargarResumenHorario(idHorarioLaboral);
      this.actualizarEmpleadosDelHorario();
    });

    this.form.get("fechaInicioVigencia")?.valueChanges.subscribe(() => {
      this.actualizarEmpleadosDelHorario();
    });
  }

  guardar(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.sweetAlert.showSwal("info-message", null);
      return;
    }

    const asignacion = new EmpleadoHorario();
    asignacion.idEmpleado = Number(this.form.get("idEmpleado")?.value);
    asignacion.idHorarioLaboral = Number(this.form.get("idHorarioLaboral")?.value);
    asignacion.fechaInicioVigencia = this.form.get("fechaInicioVigencia")?.value as Date;
    asignacion.estatus = "ACTIVO";

    this.saving = true;
    this.horarioService
      .asignarHorario(asignacion)
      .pipe(finalize(() => (this.saving = false)))
      .subscribe({
        next: (response) => {
          if (response?.pCodRetorno === "00000") {
            this.asignacionesCache.delete(asignacion.idEmpleado);
            this.sweetAlert.showSwal("success-message", null);
            this.consultarAsignaciones(asignacion.idEmpleado);
            this.consultarJornada(asignacion.idEmpleado);
            this.actualizarEmpleadosDelHorario(true);
            return;
          }
          this.sweetAlert.showSwal("error-message-msg", response?.pMensaje);
        },
        error: (error) => {
          this.sweetAlert.showSwal(
            "error-message-msg",
            error?.error?.pMensaje || "No se pudo asignar el horario."
          );
        },
      });
  }

  regresar(): void {
    this.router.navigate(["/TAREA/horarios"]);
  }

  crearHorario(): void {
    this.router.navigate(["/TAREA/horarios/alta"]);
  }

  seleccionarEmpleado(idEmpleado: number): void {
    this.form.patchValue({ idEmpleado });
  }

  getEmpleadoNombre(empleado: Empleado): string {
    const persona = empleado?.persona;
    if (!persona) {
      return `Empleado ${empleado?.idEmpleado ?? "-"}`;
    }
    return [persona.nombre, persona.apellidoPaterno, persona.apellidoMaterno]
      .filter(Boolean)
      .join(" ");
  }

  getEmpleadoBadge(empleado: Empleado): string {
    const persona = empleado?.persona;
    if (!persona) {
      return `ID ${empleado?.idEmpleado ?? "-"}`;
    }

    const pieces = [persona.correoelectronico, persona.telefono].filter(Boolean);
    return pieces[0] || `ID ${empleado?.idEmpleado ?? "-"}`;
  }

  formatDate(value: Date | string | null | undefined): string {
    if (!value) {
      return "-";
    }
    const date = value instanceof Date ? value : new Date(value);
    return Number.isNaN(date.getTime()) ? "-" : date.toLocaleDateString("es-MX");
  }

  formatTime(value: Date | string | null | undefined): string {
    if (!value) {
      return "-";
    }
    const raw = String(value);
    const match = raw.match(/(\d{2}:\d{2})/);
    return match ? match[1] : raw;
  }

  getJornadaComidas(): string {
    if (!this.jornadaActual?.bloquesComida?.length) {
      return "Sin comida configurada";
    }
    return this.jornadaActual.bloquesComida
      .map((bloque) => `${this.formatTime(bloque.horaInicio)} - ${this.formatTime(bloque.horaFin)}`)
      .join(", ");
  }

  getHorarioResumen(): string {
    if (!this.horarioSeleccionado?.dias?.length) {
      return "Sin días configurados";
    }

    return this.horarioSeleccionado.dias
      .map((dia) => {
        const bloques = dia.bloquesComida?.length ? ` · ${dia.bloquesComida.length} comida${dia.bloquesComida.length > 1 ? "s" : ""}` : "";
        return `${this.getDayLabel(dia.diaSemana)} ${this.formatTime(dia.horaEntrada)}-${this.formatTime(dia.horaSalida)}${bloques}`;
      })
      .join(" | ");
  }

  isSelectedEmpleado(idEmpleado: number): boolean {
    return Number(this.form.get("idEmpleado")?.value) === idEmpleado;
  }

  private cargarCatalogos(): void {
    this.loading = true;

    this.empleadoService.consultaEmpleadosActivos().subscribe({
      next: (response) => {
        this.empleados = response?.list ?? [];
        this.filtrarEmpleados(this.empleadoFilterCtrl.value ?? "");
      },
      error: () => {
        this.empleados = [];
        this.empleadosFiltrados = [];
      },
    });

    this.horarioService
      .consultarHorarios({ estatus: "ACTIVO" } as HorarioLaboral)
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: (response) => {
          this.horarios = response?.list ?? [];
        },
        error: () => {
          this.horarios = [];
        },
      });
  }

  private consultarAsignaciones(idEmpleado: number): void {
    this.obtenerAsignacionesEmpleado(idEmpleado, true).subscribe((asignaciones) => {
      this.asignacionesEmpleado = asignaciones;
    });
  }

  private consultarJornada(idEmpleado: number): void {
    const jornada = new JornadaLaboral();
    jornada.idEmpleado = idEmpleado;
    jornada.fechaReferencia = this.form.get("fechaInicioVigencia")?.value || new Date();

    this.horarioService.consultarJornada(jornada).subscribe({
      next: (response) => {
        this.jornadaActual = response?.pCodRetorno === "00000" ? response : null;
      },
      error: () => {
        this.jornadaActual = null;
      },
    });
  }

  private cargarResumenHorario(idHorarioLaboral: number | null): void {
    if (!idHorarioLaboral) {
      this.horarioSeleccionado = null;
      return;
    }

    this.loadingHorarioDetalle = true;
    this.horarioService
      .obtenerHorario(Number(idHorarioLaboral))
      .pipe(finalize(() => (this.loadingHorarioDetalle = false)))
      .subscribe({
        next: (horario) => {
          this.horarioSeleccionado =
            horario?.pCodRetorno === "00000" || !horario?.pCodRetorno ? horario : null;
        },
        error: () => {
          this.horarioSeleccionado = null;
        },
      });
  }

  private actualizarEmpleadosDelHorario(forceReload = false): void {
    const idHorario = Number(this.form.get("idHorarioLaboral")?.value);
    const fecha = this.form.get("fechaInicioVigencia")?.value as Date | null;

    if (!idHorario || !fecha || !this.empleados.length) {
      this.empleadosDelHorario = [];
      return;
    }

    this.loadingEmpleadosHorario = true;
    this.obtenerAsignacionesTodos(forceReload)
      .pipe(finalize(() => (this.loadingEmpleadosHorario = false)))
      .subscribe((asignacionesPorEmpleado) => {
        const referencia = new Date(fecha);
        this.empleadosDelHorario = this.empleados
          .map((empleado, index) => {
            const asignacionVigente = this.resolverAsignacionVigente(asignacionesPorEmpleado[index], referencia);
            return asignacionVigente && asignacionVigente.idHorarioLaboral === idHorario
              ? { empleado, asignacion: asignacionVigente }
              : null;
          })
          .filter((item): item is EmpleadoHorarioResumen => !!item)
          .sort((a, b) => this.getEmpleadoNombre(a.empleado).localeCompare(this.getEmpleadoNombre(b.empleado), "es"));
      });
  }

  private obtenerAsignacionesTodos(forceReload: boolean) {
    return forkJoin(
      this.empleados.map((empleado) =>
        this.obtenerAsignacionesEmpleado(empleado.idEmpleado, forceReload).pipe(
          catchError(() => of([] as EmpleadoHorario[]))
        )
      )
    );
  }

  private obtenerAsignacionesEmpleado(idEmpleado: number, forceReload = false) {
    if (!forceReload && this.asignacionesCache.has(idEmpleado)) {
      return of(this.asignacionesCache.get(idEmpleado) ?? []);
    }

    return this.horarioService.consultarAsignaciones(idEmpleado).pipe(
      map((response) => response?.list ?? []),
      map((asignaciones) => {
        this.asignacionesCache.set(idEmpleado, asignaciones);
        return asignaciones;
      }),
      catchError(() => {
        this.asignacionesCache.set(idEmpleado, []);
        return of([] as EmpleadoHorario[]);
      })
    );
  }

  private resolverAsignacionVigente(asignaciones: EmpleadoHorario[], fechaReferencia: Date): EmpleadoHorario | null {
    const fecha = new Date(fechaReferencia);

    return (
      asignaciones.find((asignacion) => {
        const inicio = this.toComparableDate(asignacion.fechaInicioVigencia);
        const fin = this.toComparableDate(asignacion.fechaFinVigencia);

        if (!inicio) {
          return false;
        }

        return inicio <= fecha && (!fin || fin >= fecha) && asignacion.estatus === "ACTIVO";
      }) ?? null
    );
  }

  private toComparableDate(value: Date | string | null | undefined): Date | null {
    if (!value) {
      return null;
    }

    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) {
      return null;
    }

    date.setHours(0, 0, 0, 0);
    return date;
  }

  private filtrarEmpleados(query: string): void {
    const search = query.trim().toLowerCase();

    if (!search) {
      this.empleadosFiltrados = [...this.empleados];
      return;
    }

    this.empleadosFiltrados = this.empleados.filter((empleado) => {
      const haystack = [
        this.getEmpleadoNombre(empleado),
        this.getEmpleadoBadge(empleado),
        empleado?.idEmpleado,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return haystack.includes(search);
    });
  }

  private getDayLabel(day: number): string {
    const labels: Record<number, string> = {
      1: "Lun",
      2: "Mar",
      3: "Mié",
      4: "Jue",
      5: "Vie",
      6: "Sáb",
      7: "Dom",
    };

    return labels[day] ?? `Día ${day}`;
  }
}
